import type { Wallet } from "@rainbow-me/rainbowkit";
import type { Chain, Transport } from "viem";

import { BSC_TESTNET_CHAIN_ID } from "./config";

type WalletConnectBootstrapOptions = {
  chain: Chain;
  transport: Transport;
};

type WalletConnectSession = {
  namespaces?: Record<
    string,
    {
      accounts?: string[];
      chains?: string[];
    }
  >;
  peer?: {
    metadata?: {
      name?: string;
      url?: string;
    };
  };
};

type WalletConnectProvider = {
  isWalletConnect?: boolean;
  session?: WalletConnectSession;
  signer?: { session?: WalletConnectSession };
};

type InjectedProviderFlags = {
  isTrust?: boolean;
  isTrustWallet?: boolean;
  providers?: InjectedProviderFlags[];
};

/** Trust Wallet's wallet-core coin id for BNB Smart Chain. */
export const TRUST_WALLET_BSC_COIN_ID = 20_000_714;

/** Matches RainbowKit's phone detection, including iPadOS desktop user agents. */
export function isMobileWalletBrowser(): boolean {
  if (typeof navigator === "undefined") return false;

  const ios =
    /iPhone|iPod|iPad/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  return /android/i.test(navigator.userAgent) || ios;
}

/** True only inside Trust Wallet's injected DApp Browser. */
export function isTrustWalletBrowser(): boolean {
  if (typeof window === "undefined") return false;

  const ethereum = (window as { ethereum?: InjectedProviderFlags }).ethereum;
  const isTrust = (provider: InjectedProviderFlags | undefined) =>
    provider?.isTrust === true || provider?.isTrustWallet === true;

  return isTrust(ethereum) || ethereum?.providers?.some(isTrust) === true;
}

function walletConnectSession(provider: unknown): WalletConnectSession | null {
  if (!provider || typeof provider !== "object") return null;

  const candidate = provider as WalletConnectProvider;
  try {
    return candidate.session ?? candidate.signer?.session ?? null;
  } catch {
    return null;
  }
}

/** The actual wallet selected in Reown's generic WalletConnect chooser. */
export function walletConnectPeerName(provider: unknown): string | null {
  const name = walletConnectSession(provider)?.peer?.metadata?.name?.trim();
  return name || null;
}

export function isTrustWalletConnectProvider(provider: unknown): boolean {
  if (!provider || typeof provider !== "object") return false;

  const candidate = provider as WalletConnectProvider;
  const session = walletConnectSession(provider);
  const identity = `${session?.peer?.metadata?.name ?? ""} ${session?.peer?.metadata?.url ?? ""}`;

  return candidate.isWalletConnect === true && /trust\s*wallet|trustwallet/i.test(identity);
}

/** Checks the chains the wallet approved for this specific WalletConnect session. */
export function walletConnectSessionSupportsChain(provider: unknown, chainId: number): boolean {
  const namespace = walletConnectSession(provider)?.namespaces?.eip155;
  const chain = `eip155:${chainId}`;

  return [...(namespace?.chains ?? []), ...(namespace?.accounts ?? [])].some(
    (value) => value === chain || value.startsWith(`${chain}:`),
  );
}

/**
 * Trust Wallet can connect to BSC mainnet over WalletConnect, but its approved
 * session does not include BSC testnet. Its injected DApp Browser is the safe
 * fallback because it can handle the add/switch request directly.
 */
export function needsTrustWalletTestnetBrowser(provider: unknown, chainId: number): boolean {
  return (
    chainId === BSC_TESTNET_CHAIN_ID &&
    isTrustWalletConnectProvider(provider) &&
    !walletConnectSessionSupportsChain(provider, chainId)
  );
}

/** Official Trust Wallet DApp Browser universal link. */
export function trustWalletDappBrowserUrl(portalUrl: string): string {
  const portal = new URL(portalUrl);
  if (portal.protocol !== "https:" && portal.protocol !== "http:") {
    throw new Error("Trust Wallet DApp Browser only accepts HTTP(S) portal URLs.");
  }

  const deepLink = new URL("https://link.trustwallet.com/open_url");
  deepLink.searchParams.set("coin_id", String(TRUST_WALLET_BSC_COIN_ID));
  deepLink.searchParams.set("url", portal.toString());
  return deepLink.toString();
}

/**
 * Starts WalletConnect on a broadly supported chain, while keeping wagmi's
 * public config limited to the deployment chain.
 *
 * Trust Wallet advertises BSC mainnet (56), but not BSC testnet (97), to the
 * WalletConnect explorer. A proposal containing only 97 is rejected before a
 * session exists, so there is no provider available to request adding 97. The
 * connector gets a private bootstrap chain and connects on it first; wagmi
 * still sees 56 as unsupported and the portal immediately offers its existing
 * switch/add-network flow for 97.
 */
export function withWalletConnectBootstrapChain(
  wallet: Wallet,
  { chain, transport }: WalletConnectBootstrapOptions,
): Wallet {
  const createWalletConnector = wallet.createConnector;

  return {
    ...wallet,
    createConnector(walletDetails) {
      const createConnector = createWalletConnector(walletDetails);

      return (config) => {
        const chains = [
          chain,
          ...config.chains.filter((configuredChain) => configuredChain.id !== chain.id),
        ] as const;
        const connector = createConnector({
          ...config,
          chains,
          transports: {
            ...config.transports,
            [chain.id]: transport,
          },
        });
        const connect = connector.connect.bind(connector);

        connector.connect = ((parameters) =>
          connect({ ...parameters, chainId: chain.id })) as typeof connector.connect;

        return connector;
      };
    },
  };
}
